import React from 'react';
import { Trans } from '@lingui/react/macro';
import { ConfiguratorGroupDto, ConfiguratorOptionDto } from '@/services/configurator/configurator.types';
import { Banner } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerOptionCard } from '../BuyerOptionCard/BuyerOptionCard';
import theme from './ImmersiveDependencyWizardModal.module.scss';

interface ImmersiveDependencyWizardStepProps {
    group: ConfiguratorGroupDto;
    triggerGroupName: string;
    triggerOptionName: string;
    knownSelections: Record<number, ConfiguratorOptionDto>;
    pendingOption: ConfiguratorOptionDto | null;
    onSelect: (option: ConfiguratorOptionDto) => void;
}

export const ImmersiveDependencyWizardStep: React.FC<ImmersiveDependencyWizardStepProps> = ({
    group,
    triggerGroupName,
    triggerOptionName,
    knownSelections,
    pendingOption,
    onSelect,
}) => {
    // Each option knows its own dependency conflicts. An option is incompatible if any of its
    // dependencyConflicts entries matches a known selection in that related group.
    const hasConstraints = group.options.some((o) =>
        o.dependencyConflicts.some((fw) => knownSelections[fw.groupId] !== undefined)
    );
    const allowedOptionIds: Set<number> | null = hasConstraints
        ? new Set(
              group.options
                  .filter((o) => !o.dependencyConflicts.some((fw) => knownSelections[fw.groupId]?.id === fw.optionId))
                  .map((o) => o.id)
          )
        : null;

    const isIncompatible = (option: ConfiguratorOptionDto) =>
        allowedOptionIds !== null && !allowedOptionIds.has(option.id);

    const pendingOptionIsIncompatible = pendingOption !== null && isIncompatible(pendingOption);

    // Only the compatible options are offered as choices (single column).
    const validOptions = group.options.filter((o) => !isIncompatible(o));

    // The option currently selected in this group that the trigger selection conflicts with — shown
    // on top (read-only, with the red indicator) so the user sees what has to be replaced.
    const conflictingOption =
        group.selectedOption && isIncompatible(group.selectedOption) ? group.selectedOption : null;

    return (
        <>
            <div className={theme.bannerWrap}>
                <Banner type={pendingOptionIsIncompatible ? 'danger' : 'warning'} icon={null}>
                    <Trans>
                        Your selection of <strong>{triggerOptionName}</strong> in <strong>{triggerGroupName}</strong>{' '}
                        affects this option group. Please review your selection below.
                    </Trans>
                </Banner>
            </div>

            <div className={theme.body}>
                {group.description && <p className={theme.groupDescription}>{group.description}</p>}

                {conflictingOption && (
                    <BuyerOptionCard
                        option={conflictingOption}
                        compactSelection={{ selected: true, disabled: true, invalid: true, onSelect: () => {} }}
                    />
                )}

                {validOptions.length > 0 && (
                    <>
                        <div className={theme.divider}>
                            <Trans>Possible options</Trans>
                        </div>
                        <div className={theme.optionList}>
                            {validOptions.map((option) => (
                                <BuyerOptionCard
                                    key={option.id}
                                    option={option}
                                    compactSelection={{
                                        selected: pendingOption?.id === option.id,
                                        disabled: false,
                                        invalid: false,
                                        onSelect: () => onSelect(option),
                                    }}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};
